import React, { useState } from 'react';
import { Trophy, TrendingUp, Gift, ChevronRight } from 'lucide-react';
import { Button } from '@/components/Button';
import { cn } from '@/lib/utils';
import { UserAvatar } from '@/components/UserAvatar';
import { usePage, router } from '@inertiajs/react';
import { RedeemModal } from '@/features/feed/RedeemModal';

export function RightSidebar() {
  const { auth, leaderboard = [], trending = [] } = usePage().props;
  const currentUser = auth?.user;
  const REDEEMABLE_POINTS = currentUser?.points_balance || 0;

  // UI States
  const [selectedValue, setSelectedValue] = useState(100);
  const [isRedeemModalOpen, setIsRedeemModalOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const handleRedeem = () => {
    setIsRedeemModalOpen(true);
  };

  const loading = false; // Data comes from server props

  return (
    <div className="space-y-6">

      {/* 1. REDEEM CARD */}
      <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 overflow-hidden relative">
        <div className="absolute top-0 right-0 w-24 h-24 bg-bambu-green/10 rounded-bl-full -mr-4 -mt-4 z-0"></div>

        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 bg-bambu-green-pastel/50 rounded-full text-bambu-dark-green">
              <Gift className="w-5 h-5" />
            </div>
            <h3 className="font-bold text-gray-800 font-montserrat">Canjear puntos</h3>
          </div>

          <div className="text-center mb-6">
            <span className="text-4xl font-black text-bambu-green tracking-tight">{REDEEMABLE_POINTS}</span>
            <span className="text-xs text-gray-500 font-bold  tracking-wider block mt-1">Puntos disponibles para canje</span>
          </div>

          {/* Selector */}
          <div className="bg-gray-50 rounded-xl p-4 mb-4 border border-gray-100">
            <label className="text-xs text-gray-400 font-bold  mb-2 block">Valor de tarjeta de regalo</label>
            <div className="flex items-center justify-between">
              <button
                onClick={() => setSelectedValue(Math.max(100, selectedValue - 100))}
                className="w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-100 active:scale-95 transition-all"
              >
                -
              </button>
              <div className="text-center">
                <span className="block text-xl font-bold text-gray-900">${selectedValue}</span>
                <span className="text-xs text-bambu-green font-bold">Costo: {selectedValue * 10} pts</span>
              </div>
              <button
                onClick={() => setSelectedValue(selectedValue + 100)}
                className="w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-100 active:scale-95 transition-all"
              >
                +
              </button>
            </div>
          </div>

          <Button
            onClick={handleRedeem}
            className="w-full rounded-xl bg-bambu-dark-green hover:bg-gray-900 text-white font-bold py-3 shadow-lg shadow-bambu-green/20"
            disabled={selectedValue * 10 > REDEEMABLE_POINTS}
          >
            Solicitar canje
          </Button>
        </div>
      </div>

      {/* 2. LEADERBOARD */}
      <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center gap-2 mb-6">
          <Trophy className="w-5 h-5 text-amber-500" />
          <h3 className="font-bold text-gray-800 font-montserrat">Top de los más destacados</h3>
        </div>

        <div className="space-y-4">
          {leaderboard.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">Sin datos aún</p>
          ) : (
            (isExpanded ? leaderboard : leaderboard.slice(0, 3)).map((user, index) => (
              <div key={user.id} className="flex items-center justify-between group cursor-pointer hover:bg-gray-50 p-2 -mx-2 rounded-xl transition-colors">
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold",
                    index === 0 ? "bg-amber-100 text-amber-600" :
                      index === 1 ? "bg-gray-100 text-gray-600" :
                        "bg-orange-50 text-orange-600"
                  )}>
                    {index + 1}
                  </div>
                  <UserAvatar user={user} size={40} className="border border-gray-100" />
                  <div>
                    <p className="font-bold text-sm text-gray-800 leading-tight group-hover:text-amber-600 transition-colors">{user.full_name}</p>
                    <p className="text-[10px] text-gray-400 font-medium">{user.dept || 'General'}</p>
                  </div>
                </div>
                <div className="text-right flex items-center justify-end">
                  <span className="font-black text-xl text-bambu-green leading-none tracking-tight">{user.recognitions}</span>
                  <span className="text-[10px] text-gray-300 ml-1">men</span>
                </div>
              </div>
            )))}
        </div>

        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full mt-4 text-xs font-bold text-gray-400 hover:text-bambu-green py-2 flex items-center justify-center gap-1 transition-colors"
        >
          {isExpanded ? (
            <>Ver menos <ChevronRight className="w-3 h-3 rotate-[-90deg]" /></>
          ) : (
            <>Ver tabla completa <ChevronRight className="w-3 h-3 rotate-90" /></>
          )}
        </button>
      </div>

      {/* 3. TRENDING HASHTAGS */}
      <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-5 h-5 text-blue-500" />
          <h3 className="font-bold text-gray-800 font-montserrat">Valores tendencia</h3>
        </div>

        <div className="space-y-3">
          {trending.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">Sin tendencias aún</p>
          ) : (
            trending.map((topic, i) => (
              <div key={i} className="flex items-center justify-between px-3 py-2.5 bg-gray-50 hover:bg-blue-50 group hover:scale-[1.02] transition-all duration-300 rounded-xl cursor-pointer border border-transparent hover:border-blue-100 shadow-sm hover:shadow-md">
                <span className="font-black text-sm text-gray-600 group-hover:text-blue-600 tracking-wide">{topic.tag}</span>
                <span className="bg-white text-xs font-black text-gray-400 px-2 py-0.5 rounded-md group-hover:text-blue-500 shadow-sm border border-gray-100">{topic.count}</span>
              </div>
            )))}
        </div>
      </div>

      <RedeemModal
        isOpen={isRedeemModalOpen}
        onClose={() => setIsRedeemModalOpen(false)}
        amount={selectedValue}
        pointsCost={selectedValue * 10}
      />
    </div >
  );
}
